import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './auth/AuthProvider';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { LoginPage } from './auth/LoginPage';
import { AppShell } from './components/layout/AppShell';
import { LoadingSpinner } from './components/common/LoadingSpinner';

/* ─── Lazy-loaded pages ─── */
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const Archive = lazy(() => import('./pages/Archive'));
const WeeklyReports = lazy(() => import('./pages/WeeklyReports'));
const InternalReports = lazy(() => import('./pages/InternalReports'));
const BarazaReports = lazy(() => import('./pages/BarazaReports'));
const CameroonIntelligence = lazy(() => import('./pages/CameroonIntelligence'));
const Settings = lazy(() => import('./pages/Settings'));
const Support = lazy(() => import('./pages/Support'));
const Bookmarks = lazy(() => import('./pages/Bookmarks'));
const Register = lazy(() => import('./pages/Register'));
const RegionalNews = lazy(() => import('./pages/RegionalNews'));
const AlertsPage = lazy(() => import('./pages/AlertsPage'));
const EntityPage = lazy(() => import('./pages/EntityPage'));
const RegionalIntelPage = lazy(() => import('./pages/RegionalIntelPage'));
const StratMinedWeekly = lazy(() => import('./pages/StratMinedWeekly'));
const StrategicBriefs = lazy(() => import('./pages/StrategicBriefs'));
const Subscription = lazy(() => import('./pages/Subscription'));

/* ─── TanStack Query client ─── */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Page loading fallback component.
 */
const PageFallback: React.FC = () => (
  <LoadingSpinner message="Loading page..." />
);

/**
 * Wrapper component for category pages to pass the slug prop.
 */
const CategoryRoute: React.FC<{ slug: string }> = ({ slug }) => (
  <Suspense fallback={<PageFallback />}>
    <CategoryPage categorySlug={slug} />
  </Suspense>
);

/**
 * Root application component.
 * Sets up authentication, data fetching, and routing.
 */
const App: React.FC = () => {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<Suspense fallback={<PageFallback />}><Register /></Suspense>} />

            {/* Root redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Protected routes wrapped in AppShell */}
            <Route
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              {/* Dashboard */}
              <Route
                path="/dashboard"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <Dashboard />
                  </Suspense>
                }
              />

              {/* Category pages */}
              <Route path="/mining-companies" element={<CategoryRoute slug="mining-companies" />} />
              <Route path="/market-insights" element={<CategoryRoute slug="market-insights" />} />
              <Route path="/commodities" element={<CategoryRoute slug="commodities" />} />
              <Route path="/africa-focus" element={<CategoryRoute slug="africa-focus" />} />
              <Route path="/financial-results" element={<CategoryRoute slug="financial-results" />} />
              <Route path="/financing" element={<CategoryRoute slug="financing" />} />
              <Route path="/infrastructure" element={<CategoryRoute slug="infrastructure" />} />
              <Route path="/regulatory" element={<CategoryRoute slug="regulatory" />} />
              <Route path="/legal-taxes-royalties" element={<CategoryRoute slug="legal-taxes-royalties" />} />
              <Route path="/artisanal-mining" element={<CategoryRoute slug="artisanal-mining" />} />
              <Route path="/government-figures" element={<CategoryRoute slug="government-figures" />} />
              <Route path="/consultancy-reports" element={<CategoryRoute slug="consultancy-reports" />} />

              {/* Internal Reports (admin only) */}
              <Route
                path="/internal-reports"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Suspense fallback={<PageFallback />}>
                      <InternalReports />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              {/* Archive */}
              <Route
                path="/archive"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <Archive />
                  </Suspense>
                }
              />

              {/* Weekly Reports */}
              <Route
                path="/weekly-reports"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <WeeklyReports />
                  </Suspense>
                }
              />

              {/* Baraza Mining Forum (admin only) */}
              <Route
                path="/baraza"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Suspense fallback={<PageFallback />}>
                      <BarazaReports />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              {/* Cameroon Intelligence (admin only) */}
              <Route
                path="/cameroon"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Suspense fallback={<PageFallback />}>
                      <CameroonIntelligence />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              {/* StratMined Weekly (admin) */}
              <Route
                path="/stratmined-weekly"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Suspense fallback={<PageFallback />}>
                      <StratMinedWeekly />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              {/* Regional News */}
              <Route
                path="/region/:code"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <RegionalNews type="region" />
                  </Suspense>
                }
              />
              <Route
                path="/rec/:code"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <RegionalNews type="rec" />
                  </Suspense>
                }
              />

              {/* Alerts (Pro+) */}
              <Route
                path="/alerts"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <AlertsPage />
                  </Suspense>
                }
              />

              {/* Entity Profiles */}
              <Route
                path="/entity/:slug"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <EntityPage />
                  </Suspense>
                }
              />

              {/* Regional Intel */}
              <Route
                path="/regional-intel/:recCode"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <RegionalIntelPage />
                  </Suspense>
                }
              />

              {/* Strategic Briefs (enterprise + staff) */}
              <Route
                path="/briefs"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <StrategicBriefs />
                  </Suspense>
                }
              />

              {/* Subscription / Billing (all authenticated users) */}
              <Route
                path="/subscription"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <Subscription />
                  </Suspense>
                }
              />

              {/* Bookmarks */}
              <Route
                path="/bookmarks"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <Bookmarks />
                  </Suspense>
                }
              />

              {/* Support */}
              <Route
                path="/support"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <Support />
                  </Suspense>
                }
              />

              {/* Settings (admin only) */}
              <Route
                path="/settings"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Suspense fallback={<PageFallback />}>
                      <Settings />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </AuthProvider>
  );
};

export default App;
